import { useCallback } from 'react';

import { DosageTimeUnit, getLockedDurationUnit } from '~libs.shared.prescription';

import { ExtendedDosageFormController } from '../ExtendedDosageForm.types';

import { DosagePeriodAmountSteps } from './DosagePeriodField.types';
import { ALLOWED_HOUR_INTERVAL_STEPS, getMultipleSteps } from './DosagePeriodField.utils';
import { createStageFieldNameFactory, setStageFieldValue } from './StageFieldList.utils';

type UseDurationUnitLockOptions = Pick<ExtendedDosageFormController, 'watch' | 'setValue'> & {
   index: number;
   intervalUnitDefaultValue: DosageTimeUnit;
};

type DurationUnitLock = {
   durationUnitLocked: boolean;
   handleIntervalUnitSelect: (unit: DosageTimeUnit | null) => void;
};

export const useDurationUnitLock = (options: UseDurationUnitLockOptions): DurationUnitLock => {
   const { setValue } = options;
   const createFieldName = createStageFieldNameFactory(options.index);
   const durationUnitName = createFieldName('duration.unit');
   const intervalUnit =
      (options.watch(createFieldName('interval.unit')) as DosageTimeUnit | undefined) ??
      options.intervalUnitDefaultValue;

   const handleIntervalUnitSelect = useCallback(
      (unit: DosageTimeUnit | null): void => {
         const lockedUnit = getLockedDurationUnit(unit);

         if (lockedUnit) {
            setStageFieldValue(setValue, durationUnitName, lockedUnit);
         }
      },
      [setValue, durationUnitName]
   );

   return {
      durationUnitLocked: Boolean(getLockedDurationUnit(intervalUnit)),
      handleIntervalUnitSelect: handleIntervalUnitSelect,
   };
};

type UseFrequencyDosageCycleAmountStepsOptions = Pick<ExtendedDosageFormController, 'watch'> & {
   index: number;
   intervalUnitDefaultValue: DosageTimeUnit;
   intervalAmountDefaultValue: number;
};

type FrequencyDosageCycleAmountSteps = {
   intervalAmountSteps: DosagePeriodAmountSteps | undefined;
   durationAmountSteps: DosagePeriodAmountSteps | undefined;
};

export const useFrequencyDosageCycleAmountSteps = (
   options: UseFrequencyDosageCycleAmountStepsOptions
): FrequencyDosageCycleAmountSteps => {
   const createFieldName = createStageFieldNameFactory(options.index);
   const intervalUnit =
      (options.watch(createFieldName('interval.unit')) as DosageTimeUnit | undefined) ??
      options.intervalUnitDefaultValue;
   const intervalAmount = Number(
      options.watch(createFieldName('interval.amount')) ?? options.intervalAmountDefaultValue
   );
   const hourInterval = intervalUnit === 'HOUR';

   return {
      intervalAmountSteps: hourInterval ? ALLOWED_HOUR_INTERVAL_STEPS : undefined,
      durationAmountSteps: hourInterval ? undefined : getMultipleSteps(intervalAmount),
   };
};
